
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { TextField, CircularProgress, Alert, InputAdornment, IconButton } from '@mui/material';
import { Eye, EyeOff, Mail, Lock, ArrowRight, MapPin } from 'lucide-react';
import { useAuth } from '../../context/AuthContext'; // ← back to AuthContext, NOT authService directly

// MUI internals (.MuiOutlinedInput-root, .MuiInputLabel-root, etc.) aren't reachable
// via plain Tailwind classes, so this stays as an sx object.
const inputSx = {
  '& .MuiOutlinedInput-root': {
    background: '#faf8f5', borderRadius: '12px !important', fontSize: 14, fontFamily: 'DM Sans,sans-serif',
    '& fieldset': { borderColor: 'var(--border)' },
    '&:hover fieldset': { borderColor: '#d4c4b0' },
    '&.Mui-focused fieldset': { borderColor: 'var(--brand)' },
  },
  '& .MuiInputLabel-root': { fontSize: 14, fontFamily: 'DM Sans,sans-serif', color: 'var(--text-muted)' },
  '& .MuiInputLabel-root.Mui-focused': { color: 'var(--brand)' },
};

const Login: React.FC = () => {
  const logoPath = '/adrojlogo.png';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // ✅ Get login() from AuthContext so setUser() fires and ProtectedRoute
  //    sees the updated user state immediately after navigation.
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // ✅ AuthContext.login() calls the real API, normalizes role to lowercase
      //    ('Admin' → 'admin'), persists to localStorage, and calls setUser().
      const user = await login(email, password);

      // ✅ Role is already lowercase here — matches ProtectedRoute's role prop
      //    ('admin' | 'brand') and the redirect logic inside it.
      navigate(user.role.toLocaleLowerCase() === 'admin' ? '/admin/dashboard' : '/brand/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-base)] flex">
      {/* Left panel */}
      <div className="hidden md:flex flex-[0_0_45%] flex-col justify-between p-12 relative overflow-hidden bg-[linear-gradient(145deg,#1a0f07_0%,#3d1e0a_50%,#1a0f07_100%)]">
        {/* Decorative circles */}
        <div className="absolute -top-20 -right-20 w-[300px] h-[300px] rounded-full border border-[rgba(232,98,26,0.15)]" />
        <div className="absolute top-5 right-5 w-[200px] h-[200px] rounded-full border border-[rgba(232,98,26,0.08)]" />
        <div className="absolute -bottom-[100px] -left-[60px] w-[350px] h-[350px] rounded-full border border-[rgba(232,98,26,0.1)]" />
        <div className="absolute bottom-[100px] left-10 w-1.5 h-1.5 rounded-full bg-[rgba(232,98,26,0.6)]" />
        <div className="absolute top-40 right-[120px] w-1 h-1 rounded-full bg-[rgba(232,98,26,0.5)]" />

        <div className="relative z-10">
          <div className="flex items-center">
            <img src={logoPath} alt="Adroj Logo" className="h-[90px] w-auto object-contain" />
          </div>
        </div>

        <div className="relative z-10">
          <h2 className="text-white font-['Playfair_Display',serif] font-bold text-[36px] leading-[1.2] mb-5">
            India's Premier Advertising Space Marketplace
          </h2>
          <p className="text-[rgba(255,255,255,0.5)] font-['DM_Sans',sans-serif] text-[15px] leading-[1.7] m-0">
            Connect brands with the best outdoor advertising spaces across 200+ cities. From billboards to digital
            screens — all in one platform.
          </p>
          <div className="flex gap-6 mt-9">
            {[
              { val: '500+', label: 'Ad Spaces' },
              { val: '50+', label: 'Cities' },
              { val: '200+', label: 'Brands' },
            ].map((s) => (
              <div key={s.label}>
                <div className="text-[#e8621a] font-['Playfair_Display',serif] font-bold text-[26px]">{s.val}</div>
                <div className="text-[rgba(255,255,255,0.4)] font-['DM_Sans',sans-serif] text-xs">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 flex gap-2 items-center">
          <MapPin size={14} color="rgba(255,255,255,0.3)" />
          <span className="text-[rgba(255,255,255,0.3)] font-['DM_Sans',sans-serif] text-xs">
            Mumbai · Delhi · Bangalore · Hyderabad · Pune · Chennai
          </span>
        </div>
      </div>

      {/* Right: Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-[420px]">
          {/* Mobile logo */}
          <div className="block md:hidden text-center mb-8">
            <div className="w-12 h-12 rounded-[14px] bg-[linear-gradient(135deg,#e8621a,#c2490e)] inline-flex items-center justify-center mb-3">
              <span className="text-white font-['Playfair_Display',serif] font-bold text-2xl">A</span>
            </div>
            <h1 className="text-[var(--text-primary)] font-['Playfair_Display',serif] font-bold text-2xl m-0">
              adroj<span className="text-[var(--brand)]">.</span>
            </h1>
          </div>

          <h2 className="text-[var(--text-primary)] font-bold text-[26px] mb-1.5 tracking-[-0.02em]">
            Welcome back
          </h2>
          <p className="text-[var(--text-muted)] text-sm mb-8">Sign in to your account to continue</p>

          {error && (
            <Alert severity="error" className="mb-5 !rounded-xl">
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <TextField
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
              sx={inputSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Mail size={15} color="var(--text-muted)" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Password"
              type={showPw ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              fullWidth
              sx={inputSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock size={15} color="var(--text-muted)" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPw(!showPw)} size="small">
                      {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <button
              type="submit"
              disabled={loading}
              className="btn-brand py-[13px] px-6 text-[15px] flex items-center justify-center gap-2 mt-1"
            >
              {loading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <>
                  Sign In <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>

          <p className="text-center text-[var(--text-muted)] text-[13px] mt-6">
            Don't have an account?{' '}
            <Link to="/register" className="text-[var(--brand)] font-semibold no-underline">
              Register here
            </Link>
          </p>
          <p className="text-center text-[var(--text-muted)] text-[8px] mt-4">
            
              V - 1.2.0
            
          </p>

          {/* Demo creds */}
          {/* <div className="mt-7 bg-[var(--bg-muted)] rounded-[14px] px-4 py-3.5 border border-[var(--border)]">
            <div className="text-[var(--text-muted)] text-[11px] font-semibold uppercase tracking-[0.06em] mb-2.5">
              Demo Credentials
            </div>
            <div className="flex flex-col gap-1.5">
              {[
                { role: 'Brand Company', email: 'brand@company.com', icon: '🏢' },
                { role: 'Admin', email: 'admin@adroj.com', icon: '⚙️' },
              ].map((c) => (
                <div
                  key={c.role}
                  onClick={() => setEmail(c.email)}
                  className="flex justify-between items-center bg-white rounded-[10px] px-3 py-2.5 cursor-pointer border border-[var(--border)] transition-colors duration-200 hover:border-[var(--brand)]"
                >
                  <span className="text-[13px] text-[var(--text-primary)] font-medium">
                    {c.icon} {c.role}
                  </span>
                  <span className="text-[11px] text-[var(--text-muted)] font-mono">{c.email}</span>
                </div>
              ))}
            </div>
            <p className="text-[var(--text-muted)] text-[11px] mt-2 mb-0">
              Click a role above to auto-fill. Any password works.
            </p>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default Login;